import React from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSubscription } from '../contexts/SubscriptionContext.tsx';

interface AdminOnlyProps {
  children: React.ReactNode;
  /** Optional element rendered for non-admin (read-only) users instead. */
  fallback?: React.ReactNode;
  /**
   * Render even when the tenant's subscription has lapsed. Use for controls that must stay
   * reachable precisely because billing is broken (anything that leads to resolving it).
   */
  allowWhenUnsubscribed?: boolean;
}

/**
 * Renders its children only when the signed-in user is in the Admin group AND the tenant
 * still has write access. Read-only ("User") accounts, and every user of a tenant whose
 * subscription has lapsed, see nothing (or the optional `fallback`).
 *
 * This is a UX affordance only — the authoritative enforcement is the backend RBAC layer
 * (backend/src/utils/auth.ts) and the subscription paywall (backend/src/utils/entitlement.ts).
 * Hiding a control here prevents accidental clicks; it does not by itself secure the API.
 */
const AdminOnly: React.FC<AdminOnlyProps> = ({ children, fallback = null, allowWhenUnsubscribed = false }) => {
  const { isAdmin } = useAuth();
  const { canWrite } = useSubscription();
  const permitted = isAdmin && (canWrite || allowWhenUnsubscribed);
  return <>{permitted ? children : fallback}</>;
};

export default AdminOnly;
